import CurrencyInput from "../components/CurrencyInput";
import PercentageInput from "../components/PercentageInput";
import { MarketStatistic } from "../components/MarketStatistic";
import { SectionHeader } from "../components/SectionHeader";
import { ShowMoreToggle } from "../components/ShowMoreToggle";
import { useIncomeLogic } from "./Income.logic";
import { useFieldStatistic } from "../hooks/useStatistics";
import { useStatisticsValue } from "../hooks/useStatisticsValue";

/**
 * IncomeForm - Pure form component (no layout)
 * Used by both IncomeSettings.mobile (with SettingsPageLayout) and SettingsModal
 * All income values are monthly
 *
 * Essential: employmentIncome, pensionIncome, investmentIncome, rentalIncome, otherIncome
 * Advanced: bonusVariableIncome, salaryGrowthRate, businessIncome
 */
export function IncomeForm() {
  const {
    formData,
    showPension,
    showCPP,
    showOAS,
    showAdvanced,
    toggleAdvanced,
    handleAmountChange,
    handleBlur,
    loading,
    totalMonthlyIncome,
  } = useIncomeLogic();

  // Get age-specific statistic value with field visibility filter
  const incomeStatistic = useStatisticsValue("income", (fieldName) => {
    // Apply the same visibility conditions as the form fields
    if (fieldName === "pensionIncome" || fieldName === "cppIncome") {
      return showPension; // Both pension and CPP require retirement
    }
    if (fieldName === "oasIncome") {
      return showOAS; // OAS requires age >= 65
    }
    return true; // All other fields are always visible
  });

  // Get statistics help text for each field
  const employmentIncomeHelp = useFieldStatistic(
    "employmentIncome",
    "Salary, wages, or self-employment income"
  );
  const pensionIncomeHelp = useFieldStatistic(
    "pensionIncome",
    "Workplace pension or RRIF withdrawals"
  );
  const cppIncomeHelp = useFieldStatistic(
    "cppIncome",
    "Canada Pension Plan benefits"
  );
  const oasIncomeHelp = useFieldStatistic(
    "oasIncome",
    "Old Age Security benefits"
  );
  const investmentIncomeHelp = useFieldStatistic(
    "investmentIncome",
    "Dividends, interest, capital gains"
  );
  const rentalIncomeHelp = useFieldStatistic(
    "rentalIncome",
    "Income from rental properties"
  );
  const otherIncomeHelp = useFieldStatistic(
    "otherIncome",
    "Any other income sources"
  );

  // Advanced field statistics
  const bonusVariableIncomeHelp = useFieldStatistic(
    "bonusVariableIncome",
    "Monthly average bonus, commissions, variable compensation"
  );
  const businessIncomeHelp = useFieldStatistic(
    "businessIncome",
    "Net monthly business income after expenses"
  );

  if (loading) {
    return null;
  }

  return (
    <div className="settings-form-container" id="income-form">
      <form className="settings-form-scroll">
        {/* ── Employment Income ── */}
        <SectionHeader
          title="Employment Income"
          sectionId="employment-income"
        />

        <div className="form-grid">
          <CurrencyInput
            name="employmentIncome"
            value={formData.employmentIncome}
            onChange={(name, value) => handleAmountChange(name, value)}
            onBlur={() => handleBlur("employmentIncome")}
            label="Employment Income"
            helpText={employmentIncomeHelp}
            suffix="/mo"
          />
        </div>

        {/* ── Government Benefits (conditional) ── */}
        {(showPension || showCPP || showOAS) && (
          <>
            <SectionHeader
              title="Government Benefits"
              sectionId="government-benefits"
            />

            <div className="form-grid">
              {showPension && (
                <CurrencyInput
                  name="pensionIncome"
                  value={formData.pensionIncome}
                  onChange={(name, value) => handleAmountChange(name, value)}
                  onBlur={() => handleBlur("pensionIncome")}
                  label="Pension Income"
                  helpText={pensionIncomeHelp}
                  suffix="/mo"
                />
              )}

              {showCPP && (
                <CurrencyInput
                  name="cppIncome"
                  value={formData.cppIncome}
                  onChange={(name, value) => handleAmountChange(name, value)}
                  onBlur={() => handleBlur("cppIncome")}
                  label="CPP Income"
                  helpText={cppIncomeHelp}
                  suffix="/mo"
                />
              )}

              {showOAS && (
                <CurrencyInput
                  name="oasIncome"
                  value={formData.oasIncome}
                  onChange={(name, value) => handleAmountChange(name, value)}
                  onBlur={() => handleBlur("oasIncome")}
                  label="OAS Income"
                  helpText={oasIncomeHelp}
                  suffix="/mo"
                />
              )}
            </div>
          </>
        )}

        {/* ── Investment & Other Income ── */}
        <SectionHeader
          title="Investment & Other Income"
          sectionId="investment-other-income"
        />

        <div className="form-grid">
          <CurrencyInput
            name="investmentIncome"
            value={formData.investmentIncome}
            onChange={(name, value) => handleAmountChange(name, value)}
            onBlur={() => handleBlur("investmentIncome")}
            label="Investment Income"
            helpText={investmentIncomeHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="rentalIncome"
            value={formData.rentalIncome}
            onChange={(name, value) => handleAmountChange(name, value)}
            onBlur={() => handleBlur("rentalIncome")}
            label="Rental Income"
            helpText={rentalIncomeHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="otherIncome"
            value={formData.otherIncome}
            onChange={(name, value) => handleAmountChange(name, value)}
            onBlur={() => handleBlur("otherIncome")}
            label="Other Income"
            helpText={otherIncomeHelp}
            suffix="/mo"
          />
        </div>

        {/* ── Advanced Fields ── */}
        <ShowMoreToggle expanded={showAdvanced} onToggle={toggleAdvanced}>
          <SectionHeader
            title="Variable Compensation"
            sectionId="variable-compensation"
          />

          <div className="form-grid">
            <CurrencyInput
              name="bonusVariableIncome"
              value={formData.bonusVariableIncome}
              onChange={(name, value) => handleAmountChange(name, value)}
              onBlur={() => handleBlur("bonusVariableIncome")}
              label="Bonus / Variable Income"
              helpText={bonusVariableIncomeHelp}
              suffix="/mo"
            />

            <PercentageInput
              name="salaryGrowthRate"
              value={formData.salaryGrowthRate}
              onChange={(name, value) => handleAmountChange(name, value)}
              onBlur={() => handleBlur("salaryGrowthRate")}
              label="Salary Growth Rate"
              min={0}
              max={20}
              helpText="Expected annual salary increase (e.g. 3%)"
            />
          </div>

          <SectionHeader title="Business Income" sectionId="business-income" />

          <div className="form-grid">
            <CurrencyInput
              name="businessIncome"
              value={formData.businessIncome}
              onChange={(name, value) => handleAmountChange(name, value)}
              onBlur={() => handleBlur("businessIncome")}
              label="Business Income"
              helpText={businessIncomeHelp}
              suffix="/mo"
            />
          </div>
        </ShowMoreToggle>
      </form>

      {/* Total (calculated, read-only) - Sticky at bottom for long forms */}
      <div className="form-sticky-save" id="income-total-bar">
        {/* Market Statistic Information - Between Total label and horizontal rule */}
        <div className="form-total-statistic-spacing">
          <MarketStatistic
            label="Canadian Monthly Income Statistic"
            marketAverage={incomeStatistic}
            currency={true}
          />
        </div>

        <div className="form-page-total-row">
          <span className="form-page-total-label">Total Monthly Income</span>
          <span id="total" className="form-page-total-value-green">
            ${totalMonthlyIncome.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
